import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Checkbox,
  Alert,
} from '@mui/material';
import {
  AdminPanelSettings as AdminIcon,
  Store as MerchantIcon,
  Person as CustomerIcon,
  LocalShipping as DeliveryIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import axios from '../api';

interface Permission {
  id: string;
  name: string;
  description: string;
}

interface RolePermissions {
  role: string;
  icon: React.ReactNode;
  color: string;
  permissions: { [key: string]: boolean };
}

const availablePermissions: Permission[] = [
  { id: 'dashboard', name: 'Dashboard', description: 'View dashboard' },
  { id: 'view_users', name: 'View Users', description: 'View all users' },
  { id: 'manage_users', name: 'Manage Users', description: 'Create, edit, delete users' },
  { id: 'view_businesses', name: 'View Businesses', description: 'View all businesses' },
  { id: 'manage_businesses', name: 'Manage Businesses', description: 'Approve/reject businesses' },
  { id: 'view_boxes', name: 'View Surprise Boxes', description: 'View all surprise boxes' },
  { id: 'manage_boxes', name: 'Manage Surprise Boxes', description: 'Create, edit surprise boxes' },
  { id: 'view_orders', name: 'View Orders', description: 'View all orders' },
  { id: 'manage_orders', name: 'Manage Orders', description: 'Update order status' },
  { id: 'view_payments', name: 'View Payments', description: 'View payment history' },
  { id: 'manage_payments', name: 'Manage Payments', description: 'Process refunds' },
  { id: 'view_analytics', name: 'View Analytics', description: 'View reports and analytics' },
  { id: 'manage_categories', name: 'Manage Categories', description: 'Create, edit categories' },
  { id: 'view_reviews', name: 'View Reviews', description: 'View reviews and ratings' },
  { id: 'manage_reviews', name: 'Manage Reviews', description: 'Delete inappropriate reviews' },
  { id: 'access_management', name: 'Access Management', description: 'Manage role permissions' },
];

const defaultRolePermissions: RolePermissions[] = [
  {
    role: 'admin',
    icon: <AdminIcon />,
    color: '#0B6E4F',
    permissions: {
      dashboard: true,
      view_users: true,
      manage_users: true,
      view_businesses: true,
      manage_businesses: true,
      view_boxes: true,
      manage_boxes: true,
      view_orders: true,
      manage_orders: true,
      view_payments: true,
      manage_payments: true,
      view_analytics: true,
      manage_categories: true,
      view_reviews: true,
      manage_reviews: true,
      access_management: true,
    },
  },
  {
    role: 'merchant',
    icon: <MerchantIcon />,
    color: '#B1D65D',
    permissions: {
      dashboard: true,
      view_businesses: true,
      view_boxes: true,
      manage_boxes: true,
      view_orders: true,
      manage_orders: true,
      view_analytics: true,
      view_reviews: true,
    },
  },
  {
    role: 'customer',
    icon: <CustomerIcon />,
    color: '#FF6B35',
    permissions: {
      dashboard: true,
      view_boxes: true,
      view_orders: true,
      view_reviews: true,
    },
  },
  {
    role: 'delivery',
    icon: <DeliveryIcon />,
    color: '#2196F3',
    permissions: {
      dashboard: true,
      view_orders: true,
      manage_orders: true,
      view_analytics: true,
    },
  },
];

// Static presentation metadata (icon + color) per role. The server stores only
// the permission map, so we attach these locally after fetching.
const ROLE_META: Record<string, { icon: React.ReactNode; color: string }> = {
  admin: { icon: <AdminIcon />, color: '#0B6E4F' },
  merchant: { icon: <MerchantIcon />, color: '#B1D65D' },
  customer: { icon: <CustomerIcon />, color: '#FF6B35' },
  delivery: { icon: <DeliveryIcon />, color: '#2196F3' },
};

const ROLE_ORDER = ['admin', 'merchant', 'customer', 'delivery'];

// Attach presentation metadata to the server's { role, permissions } records
// and order them consistently.
const withMeta = (
  list: { role: string; permissions: { [key: string]: boolean } }[]
): RolePermissions[] =>
  [...list]
    .sort((a, b) => ROLE_ORDER.indexOf(a.role) - ROLE_ORDER.indexOf(b.role))
    .map((r) => ({
      role: r.role,
      permissions: r.permissions || {},
      icon: ROLE_META[r.role]?.icon ?? <AdminIcon />,
      color: ROLE_META[r.role]?.color ?? '#6E6E68',
    }));

export default function AccessManagement() {
  const [rolePermissions, setRolePermissions] = useState<RolePermissions[]>(defaultRolePermissions);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedRole, setSelectedRole] = useState<RolePermissions | null>(null);
  const [tempPermissions, setTempPermissions] = useState<{ [key: string]: boolean }>({});
  const [showSuccess, setShowSuccess] = useState(false);
  const [saving, setSaving] = useState(false);
  const [apiError, setApiError] = useState('');

  // Load the saved permission maps from the backend on mount. If the request
  // fails (e.g. backend offline) we keep the built-in defaults and show a note.
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await axios.get('/api/admin/permissions');
        if (active && Array.isArray(res.data) && res.data.length) {
          setRolePermissions(withMeta(res.data));
          setApiError('');
        }
      } catch (err) {
        if (active) setApiError('Could not load saved permissions from the server. Showing defaults.');
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  const handleEditRole = (role: RolePermissions) => {
    setSelectedRole(role);
    setTempPermissions({ ...role.permissions });
    setEditDialogOpen(true);
  };

  const handlePermissionToggle = (permissionId: string) => {
    setTempPermissions((prev) => ({
      ...prev,
      [permissionId]: !prev[permissionId],
    }));
  };

  const handleSavePermissions = async () => {
    if (!selectedRole) {
      setEditDialogOpen(false);
      return;
    }
    setSaving(true);
    try {
      await axios.put('/api/admin/permissions', {
        role: selectedRole.role,
        permissions: tempPermissions,
      });
      setRolePermissions((prev) =>
        prev.map((r) =>
          r.role === selectedRole.role ? { ...r, permissions: tempPermissions } : r
        )
      );
      setApiError('');
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 3000);
      setEditDialogOpen(false);
    } catch (err) {
      setApiError('Failed to save permissions. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const getPermissionCount = (permissions: { [key: string]: boolean }) => {
    return Object.values(permissions).filter(Boolean).length;
  };

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
          Access Management
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Manage role-based access control and permissions
        </Typography>
      </Box>

      {showSuccess && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setShowSuccess(false)}>
          Permissions updated successfully!
        </Alert>
      )}

      {apiError && (
        <Alert severity="warning" sx={{ mb: 3 }} onClose={() => setApiError('')}>
          {apiError}
        </Alert>
      )}

      {/* Role Overview Cards */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' }, gap: 2, mb: 4 }}>
        {rolePermissions.map((role) => (
          <Card key={role.role}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Box sx={{ bgcolor: role.color, borderRadius: 2, p: 1, mr: 2, color: 'white' }}>
                  {role.icon}
                </Box>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 600, textTransform: 'capitalize' }}>
                    {role.role}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {getPermissionCount(role.permissions)} permissions
                  </Typography>
                </Box>
              </Box>
              <Button
                variant="outlined"
                size="small"
                fullWidth
                startIcon={<EditIcon />}
                onClick={() => handleEditRole(role)}
              >
                Edit Permissions
              </Button>
            </CardContent>
          </Card>
        ))}
      </Box>

      {/* Permissions Matrix */}
      <Card>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
            Permissions Matrix
          </Typography>
          <TableContainer component={Paper} elevation={0}>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: 'background.default' }}>
                  <TableCell><strong>Permission</strong></TableCell>
                  <TableCell align="center"><strong>Admin</strong></TableCell>
                  <TableCell align="center"><strong>Merchant</strong></TableCell>
                  <TableCell align="center"><strong>Customer</strong></TableCell>
                  <TableCell align="center"><strong>Delivery</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {availablePermissions.map((permission) => (
                  <TableRow key={permission.id} hover>
                    <TableCell>
                      <Typography sx={{ fontWeight: 600 }}>{permission.name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {permission.description}
                      </Typography>
                    </TableCell>
                    {rolePermissions.map((role) => (
                      <TableCell key={role.role} align="center">
                        {role.permissions[permission.id] ? (
                          <Chip label="✓" color="success" size="small" />
                        ) : (
                          <Chip label="✗" size="small" />
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Edit Permissions Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {selectedRole && (
              <>
                <Box sx={{ bgcolor: selectedRole.color, borderRadius: 2, p: 1, color: 'white' }}>
                  {selectedRole.icon}
                </Box>
                <Box>
                  <Typography variant="h6" sx={{ textTransform: 'capitalize' }}>
                    Edit {selectedRole.role} Permissions
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Select permissions for this role
                  </Typography>
                </Box>
              </>
            )}
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, pt: 2 }}>
            {availablePermissions.map((permission) => (
              <FormControlLabel
                key={permission.id}
                control={
                  <Checkbox
                    checked={!!tempPermissions[permission.id]}
                    onChange={() => handlePermissionToggle(permission.id)}
                  />
                }
                label={
                  <Box>
                    <Typography sx={{ fontWeight: 600 }}>{permission.name}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {permission.description}
                    </Typography>
                  </Box>
                }
              />
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)} disabled={saving}>Cancel</Button>
          <Button onClick={handleSavePermissions} variant="contained" disabled={saving}>
            {saving ? 'Saving…' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
